import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Chip,
  CircularProgress, 
  Container, 
  Divider, 
  Grid, 
  TextField, 
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Search as SearchIcon, 
  LocalShipping, 
  CheckCircle, 
  Error as ErrorIcon,
  Schedule,
  LocationOn,
  Person
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import shipmentService from '../../services/shipmentService';
import { ShipmentWithTracking } from '../../types/shipment';

const TrackingPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [shipment, setShipment] = useState<ShipmentWithTracking | null>(null);

  const statusIcons: Record<string, React.ReactNode> = {
    delivered: <CheckCircle color="success" />,
    in_transit: <LocalShipping color="info" />,
    out_for_delivery: <LocalShipping color="primary" />,
    pending: <Schedule color="warning" />,
    exception: <ErrorIcon color="error" />,
    returned: <ErrorIcon color="secondary" />
  };

  const statusColors: Record<string, string> = {
    delivered: theme.palette.success.main,
    in_transit: theme.palette.info.main,
    out_for_delivery: theme.palette.primary.main,
    pending: theme.palette.warning.main,
    exception: theme.palette.error.main,
    returned: theme.palette.secondary.main
  };

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      setError('Please enter a tracking number');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await shipmentService.trackShipment(trackingNumber.trim());
      setShipment(data);
    } catch (err) {
      setError(t('tracking.notFound'));
      setShipment(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: t('shipments.status.pending'),
      in_transit: t('shipments.status.in_transit'),
      out_for_delivery: t('shipments.status.out_for_delivery'),
      delivered: t('shipments.status.delivered'),
      exception: t('shipments.status.exception'),
      returned: t('shipments.status.returned')
    };
    return statusMap[status] || status;
  };

  return (
    <Container maxWidth="md">
      <Box my={8} textAlign="center">
        <Typography variant="h3" component="h1" gutterBottom>
          {t('tracking.title')}
        </Typography>
        <Typography color="textSecondary" paragraph>
          {t('tracking.subtitle')}
        </Typography>

        <Box 
          component="form" 
          onSubmit={handleTrack}
          sx={{ 
            maxWidth: 600, 
            mx: 'auto',
            mt: 6,
            mb: 8
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('tracking.placeholder')}
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                error={!!error && !loading}
                helperText={error && !loading ? error : ''}
                disabled={loading}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading || !trackingNumber.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ height: '56px' }}
              >
                {loading ? t('common.searching') : t('common.track')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {shipment && (
          <Card sx={{ mt: 4, textAlign: 'left' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <div>
                  <Typography variant="overline" color="textSecondary">
                    {t('tracking.trackingNumber')}
                  </Typography>
                  <Typography variant="h5" gutterBottom>
                    {shipment.tracking_number}
                  </Typography>
                </div>
                <Chip
                  label={getStatusLabel(shipment.status)}
                  style={{
                    backgroundColor: statusColors[shipment.status] || theme.palette.grey[500],
                    color: 'white',
                    fontWeight: 'bold',
                    minWidth: 120
                  }}
                />
              </Box>

              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocationOn color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        {t('shipments.fields.origin')}
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {shipment.origin}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocationOn color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">
                        {t('shipments.fields.destination')}
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {shipment.destination}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                {t('tracking.shipmentDetails')}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    {t('shipments.fields.weight')}
                  </Typography>
                  <Typography variant="body1">
                    {shipment.weight} kg
                  </Typography>
                </Grid>
                {shipment.customer_name && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      {t('shipments.fields.customer')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Person color="action" sx={{ mr: 0.5, fontSize: '1rem' }} />
                      <Typography variant="body1">
                        {shipment.customer_name}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {shipment.driver_name && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      {t('shipments.fields.driver')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <LocalShipping color="action" sx={{ mr: 0.5, fontSize: '1rem' }} />
                      <Typography variant="body1">
                        {shipment.driver_name}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {shipment.zone_name && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      {t('shipments.fields.zone')}
                    </Typography>
                    <Typography variant="body1">
                      {shipment.zone_name}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                {t('shipments.trackingHistory')}
              </Typography>

              {shipment.tracking_events && shipment.tracking_events.length > 0 ? (
                <List disablePadding>
                  {shipment.tracking_events.map((event, index) => (
                    <React.Fragment key={event.id}>
                      <ListItem alignItems="flex-start" disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {statusIcons[event.status] || <Schedule color="disabled" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" color="textPrimary">
                              {getStatusLabel(event.status)}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="textPrimary"
                                display="block"
                              >
                                {format(new Date(event.created_at), 'PPPpp')}
                              </Typography>
                              {event.location && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="textSecondary"
                                  display="flex"
                                  alignItems="center"
                                >
                                  <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
                                  {event.location}
                                </Typography>
                              )}
                              {event.notes && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="textSecondary"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  {event.notes}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                      {index < shipment.tracking_events.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary" align="center" py={2}>
                  {t('shipments.noTrackingHistory')}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default TrackingPage;
